import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@rentspree/component-2023.components.atoms.typography'

interface PoliciesProps {
  leasingInformation: LeasingInformation
}

export const generatePetPolicyTexts = (petDetails: LeasingInformation['petDetails']): string[] => {
  if (!petDetails || !petDetails.arePetsAllowed) {
    return ['No pet allowed']
  }

  const { allowedPets, maximumPets, isPetPolicyNegotiable, additionalPetRules } = petDetails
  const texts: string[] = []
  switch (allowedPets.length) {
    case 0: {
      break
    }
    case 1: {
      texts.push(`${allowedPets[0]} allowed`)
      break
    }
    case 2: {
      texts.push(`${allowedPets.join(' & ')} allowed`)
      break
    }
    default: {
      const mutableAllowedPets = [...allowedPets]
      const lastPet = mutableAllowedPets.pop()
      texts.push(`${mutableAllowedPets.join(', ')} & ${lastPet} allowed`)
    }
  }
  let secondLine = `Up to ${maximumPets} pet(s).`
  if (isPetPolicyNegotiable) {
    secondLine += ' Pet policy negotiable.'
  }
  texts.push(secondLine)

  if (additionalPetRules) {
    texts.push(additionalPetRules)
  }
  return texts
}

const Policies: React.FC<PoliciesProps> = ({ leasingInformation }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '16px',
        flexDirection: 'column',
        paddingBottom: '40px',
      }}
    >
      <Box>
        <Typography variant="h3" fontSize={isMobile ? 24 : 32}>
          Policies
        </Typography>
      </Box>
      <Box key="smoke-policy">
        <Typography variant="body-large">
          {leasingInformation.isSmokingAllowed ? 'Smoking allowed' : 'No smoking'}
        </Typography>
      </Box>
      <Box key="pet-policy">
        {generatePetPolicyTexts(leasingInformation.petDetails).map((text) => (
          <Typography key={text} component="div" variant="body-large">
            {text}
          </Typography>
        ))}
      </Box>
      <Box key="renter-insurance-policy">
        <Typography component="div" variant="body-large">
          {leasingInformation.isInsuranceRequired
            ? 'Renter’s insurance required'
            : 'No renter’s insurance required'}
        </Typography>
      </Box>
    </Box>
  )
}

export default Policies
